

/* ==========================================================================
   HARDWARE INSIGHTS / POPULARITY / NEAR FIT (/database/js/hardware-insights.js)

   The right telemetry dock uses one shared card language for:
   - deterministic prospective Near Fit hardware;
   - popularity/activity-ranked hardware.

   Near Fit remains subordinate to /db's deterministic compatibility engine. Edge-AI is
   only an advisory residual-risk badge for already deterministic-fitting candidates.
   ========================================================================== */

import {
  state, getCaseModeSelection, getCaseStateKey, getSelectedComponentIdentity,
  setSelectedComponent
} from '../state.js?v=20260819-audit2';
import { debugRuntimeIssue, stableKey } from '../runtime.js?v=20260819-audit2';
import { escapeHtml, parseSpecNum } from '../utils.js?v=20260819-audit2';
import { getEffectiveSpecs, checkCompatibility } from '../compatibility-engine.js?v=20260819-audit2';
import { buildAirCoolerVector, buildAioVector, buildGpuVector, weightedProximity } from '../vector-engine.js?v=20260819-audit2';
import { predictHardwareClearanceBatch } from '../ml-engine.js?v=20260819-phase35';
import { findHwVal, getHwName } from './ui-helpers.js?v=20260819-audit2';
import { catalogIdOf, resolveCatalogId } from '../catalog-identity.js?v=20260819-audit2';
import { normalizePsuFormFactor } from './hardware-index.js?v=20260819-audit2';

let popularHardwareRenderGeneration = 0;
let hardwareRecommendationRenderGeneration = 0;
let popularTelemetryCache = { fetchedAt: 0, value: null };
let hardwareRecommendationIndexSource = null;
let hardwareRecommendationIndex = null;
const hardwareRecommendationCache = new Map();
const HARDWARE_RECOMMENDATION_CACHE_LIMIT = 128;

let insightsMode = 'near';
let nearFitType = 'gpu';
let popularType = 'gpu';
let lastNearContext = { caseObj: null, formInputs: null, evalResult: null };
let lastPopularContext = { activeCase: null, currentForm: null };
let insightsBound = false;

function getHardwareSource() {
  let hw = state.hardwareGlobal;
  if (hw?.hardware && typeof hw.hardware === 'object') hw = hw.hardware;
  return hw && typeof hw === 'object' ? hw : null;
}

function normalizeMoboFormFactor(value = '') {
  const raw = String(value || '').trim().toUpperCase().replace(/[_ ]+/g, '-');
  if (!raw) return '';
  if (raw.includes('MICRO') || raw.includes('M-ATX') || raw === 'MATX') return 'MATX';
  if (raw.includes('MINI') || raw.includes('M-ITX') || raw === 'MITX' || raw === 'ITX') return 'ITX';
  if (raw.includes('DTX')) return 'DTX';
  if (raw.includes('ATX')) return 'ATX';
  return '';
}

function getRecommendationHardwareIndex() {
  const hw = getHardwareSource();
  if (!hw) return null;
  if (hardwareRecommendationIndexSource === hw && hardwareRecommendationIndex) return hardwareRecommendationIndex;

  const coolers = [...(hw.coolers_lt_70 || []), ...(hw.coolers_gt_70 || []), ...(hw.coolers || [])].map(rawItem => ({
    rawItem,
    name: getHwName(rawItem, 'air'),
    height: parseSpecNum(findHwVal(rawItem, ['height', 'coolerheight', 'size', 'heightmm']), 0),
    catalogId: catalogIdOf(rawItem)
  })).filter(item => item.name);

  const rawAios = Array.isArray(hw.aios) ? hw.aios : (Array.isArray(hw.aio_coolers) ? hw.aio_coolers : []);
  const aios = rawAios.map(rawItem => {
    const radSize = parseSpecNum(findHwVal(rawItem, ['radiatortype', 'type', 'size', 'radiatorsize', 'radiator']), 0);
    const name = getHwName(rawItem, 'aio');
    return {
      rawItem, name, radSize,
      pumpHeight: parseSpecNum(findHwVal(rawItem, ['cpublockheight', 'blockheight', 'pumpheight', 'height', 'pump_height']), 45),
      radThick: parseSpecNum(findHwVal(rawItem, ['radiatorthickness', 'thickness', 'radthick', 'radiator_thickness']), 27),
      radLen: parseSpecNum(findHwVal(rawItem, ['radiatorlength', 'length', 'radlen', 'radiator_length']), radSize === 360 ? 395 : (radSize === 280 ? 315 : (radSize === 240 ? 275 : 155))),
      catalogId: catalogIdOf(rawItem)
    };
  }).filter(item => item.name);

  const rawGpus = [...(hw.gpus_lt_215 || []), ...(hw.gpus_gt_215 || [])];
  const gpus = rawGpus.map(rawItem => {
    const name = getHwName(rawItem, 'gpu');
    const lower = name.toLowerCase();
    return {
      rawItem, name,
      len: parseSpecNum(findHwVal(rawItem, ['length', 'gpulength', 'len', 'lengthmm']), 0),
      thick: parseSpecNum(findHwVal(rawItem, ['thickness', 'thick', 'gputhickness', 'thicknessmm']), 0),
      slotClass: parseSpecNum(findHwVal(rawItem, ['slot', 'slots', 'bracket', 'pcie']), 0),
      height: parseSpecNum(findHwVal(rawItem, ['width', 'height', 'gpuheight', 'heightmm']), 137),
      isRadeon: lower.includes('radeon') || lower.includes('rx ') || lower.includes('amd') || /rx\s*90[67]0/.test(lower),
      isNvidia: lower.includes('rtx') || lower.includes('geforce') || lower.includes('nvidia') || /rtx\s*50[6789]0/.test(lower),
      catalogId: catalogIdOf(rawItem)
    };
  }).filter(item => item.name);

  const psus = [];
  const motherboards = [];
  const seenPsu = new Set();
  const seenMobo = new Set();

  for (const [bucket, rows] of Object.entries(hw)) {
    if (!Array.isArray(rows)) continue;
    const key = bucket.toLowerCase();

    if (/psu|power.?supply/.test(key)) {
      for (const rawItem of rows) {
        if (!rawItem || typeof rawItem !== 'object') continue;
        const name = getHwName(rawItem, 'psu');
        const formFactor = normalizePsuFormFactor(findHwVal(rawItem, ['form_factor', 'formfactor', 'psu_type', 'type', 'size', 'format']));
        if (!name || !formFactor) continue;
        const unique = `${name}|${formFactor}`.toLowerCase();
        if (seenPsu.has(unique)) continue;
        seenPsu.add(unique);
        psus.push({
          rawItem, name, formFactor,
          length: parseSpecNum(findHwVal(rawItem, ['length_mm', 'length', 'psulength', 'depth', 'depth_mm']), 0),
          width: parseSpecNum(findHwVal(rawItem, ['width_mm', 'width']), 0),
          height: parseSpecNum(findHwVal(rawItem, ['height_mm', 'height']), 0),
          wattage: parseSpecNum(findHwVal(rawItem, ['wattage', 'watts', 'power']), 0),
          catalogId: catalogIdOf(rawItem)
        });
      }
    }

    if (/(motherboard|mobo|mitx|matx|itx.?board|\bboards?\b)/.test(key) &&
        !/(case|gpu|cooler|fan|psu|cpu|riser)/.test(key)) {
      for (const rawItem of rows) {
        if (!rawItem || typeof rawItem !== 'object') continue;
        const name = getHwName(rawItem, 'mobo');
        let formFactor = normalizeMoboFormFactor(findHwVal(rawItem, ['form_factor', 'formfactor', 'size', 'type']));
        if (!formFactor) {
          if (key.includes('matx')) formFactor = 'MATX';
          else if (key.includes('itx')) formFactor = 'ITX';
        }
        if (!name || !formFactor) continue;
        const unique = `${name}|${formFactor}`.toLowerCase();
        if (seenMobo.has(unique)) continue;
        seenMobo.add(unique);
        motherboards.push({
          rawItem, name, formFactor,
          socket: String(findHwVal(rawItem, ['socket']) || '').trim(),
          chipset: String(findHwVal(rawItem, ['chipset']) || '').trim(),
          catalogId: catalogIdOf(rawItem)
        });
      }
    }
  }

  hardwareRecommendationIndexSource = hw;
  hardwareRecommendationIndex = { coolers, aios, gpus, psus, motherboards };
  hardwareRecommendationCache.clear();
  return hardwareRecommendationIndex;
}

function insertTopK(list, item, limit, compare) {
  let lo = 0;
  let hi = list.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (compare(item, list[mid]) < 0) hi = mid;
    else lo = mid + 1;
  }
  list.splice(lo, 0, item);
  if (list.length > limit) list.pop();
}

function rememberRecommendation(key, value) {
  hardwareRecommendationCache.set(key, value);
  while (hardwareRecommendationCache.size > HARDWARE_RECOMMENDATION_CACHE_LIMIT) {
    hardwareRecommendationCache.delete(hardwareRecommendationCache.keys().next().value);
  }
  return value;
}

async function evaluateEdgeAiBatch(caseObj, candidates, contextLabel) {
  if (!caseObj || !Array.isArray(candidates) || candidates.length === 0) return [];
  const badges = new Array(candidates.length);
  const mlRequests = [];
  const mlIndexes = [];

  candidates.forEach((candidate, idx) => {
    const deterministic = candidate.deterministic;
    if (!deterministic?.fits) {
      badges[idx] = { text: 'NO FIT', class: 'fit-fail' };
      return;
    }
    if (!candidateNeedsMl(deterministic, candidate.testForm, candidate.type)) {
      badges[idx] = {
        text: deterministic.unknowns?.length ? 'VERIFY' : 'VERIFIED',
        class: deterministic.unknowns?.length ? 'fit-warn' : 'fit-pass'
      };
      return;
    }
    mlIndexes.push(idx);
    mlRequests.push({ caseObj, formInputs: candidate.testForm, precomputedEval: deterministic });
  });

  if (mlRequests.length) {
    try {
      const results = await predictHardwareClearanceBatch(mlRequests);
      results.forEach((aiResult, requestIdx) => {
        const targetIdx = mlIndexes[requestIdx];
        const deterministic = candidates[targetIdx].deterministic;
        if (!aiResult) {
          badges[targetIdx] = {
            text: deterministic.unknowns?.length ? 'VERIFY' : 'VERIFIED',
            class: deterministic.unknowns?.length ? 'fit-warn' : 'fit-pass'
          };
          return;
        }
        const hasResidualRisk = aiResult.modelAvailable !== false && Number.isFinite(aiResult.residualRiskScore);
        if (!hasResidualRisk) {
          badges[targetIdx] = { text: 'VERIFIED', class: 'fit-pass' };
          return;
        }
        const level = aiResult.residualRiskLevel || 'LOW';
        badges[targetIdx] = {
          text: level === 'HIGH' ? `VERIFY · ${level} RISK` : `VERIFIED · ${level} RISK`,
          class: level === 'HIGH' ? 'fit-warn' : 'fit-pass'
        };
      });
    } catch (error) {
      debugRuntimeIssue(contextLabel, error);
      mlIndexes.forEach(idx => {
        badges[idx] = {
          text: candidates[idx].deterministic?.unknowns?.length ? 'VERIFY' : 'VERIFIED',
          class: candidates[idx].deterministic?.unknowns?.length ? 'fit-warn' : 'fit-pass'
        };
      });
    }
  }

  return badges;
}

function candidateNeedsMl(deterministicResult, testForm, type) {
  if (!deterministicResult?.fits || deterministicResult.unknowns?.length) return false;
  if (deterministicResult.warns?.length) return true;
  const specs = deterministicResult.activeSpecs || {};
  const known = specs.known || {};
  if (type === 'gpu') {
    const margins = [];
    if (known.maxGPU && specs.maxGPU > 0) margins.push((Number(specs.maxGPU) - Number(testForm.gpuL || 0)) / 20);
    if (known.maxGPUT && specs.maxGPUT > 0) margins.push((Number(specs.maxGPUT) - Number(testForm.gpuT || 0)) / 8);
    if (known.maxGPUH && specs.maxGPUH > 0) margins.push((Number(specs.maxGPUH) - Number(testForm.gpuH || 0)) / 10);
    return margins.length ? Math.min(...margins) <= 1 : false;
  }
  if (type === 'air') {
    return known.maxCPU && specs.maxCPU > 0 ? (Number(specs.maxCPU) - Number(testForm.cpuH || 0)) <= 10 : false;
  }
  if (type === 'aio') {
    return known.maxAIO && specs.maxAIO > 0 ? (Number(specs.maxAIO) - Number(testForm.rad || 0)) <= 40 : false;
  }
  return false;
}

export function getTopNearestHardware(caseObj, formInputs, evalResult) {
  if (!state.hardwareGlobal || !caseObj) return null;

  const index = getRecommendationHardwareIndex();
  if (!index) return null;

  const baseForm = formInputs || {};
  const activeModeId = getCaseModeSelection(caseObj, 'default');
  const effSpecs = evalResult?.activeSpecs || getEffectiveSpecs(caseObj, activeModeId);

  // Use the exact final constraints returned by checkCompatibility(). Do not
  // replace them with raw mode/spine maxima after dynamic rules have been applied.
  const maxCpu = Number(effSpecs.maxCPU || 0);
  const maxGpuT = Number(effSpecs.maxGPUT || 0);
  const maxGpuL = Number(effSpecs.maxGPU || 0);
  const maxGpuH = Number(effSpecs.maxGPUH || 0);
  const maxAio = Number(effSpecs.maxAIO || 0);

  const recCacheKey = stableKey([
    'nearest-hw-v2',
    getCaseStateKey(caseObj), activeModeId,
    maxCpu, maxGpuL, maxGpuT, maxGpuH, maxAio,
    baseForm.mobo || '', baseForm.psu || '', baseForm.cooling || '',
    Number(baseForm.cpuH || 0), Number(baseForm.gpuL || 0),
    Number(baseForm.gpuT || 0), Number(baseForm.gpuH || 0),
    Number(baseForm.rad || 0), baseForm.radFan || '',
    Number(baseForm.pcie || 0)
  ]);
  const cached = hardwareRecommendationCache.get(recCacheKey);
  if (cached) return cached;

  const coolerTop = [];
  for (const c of index.coolers) {
    const h = c.height;
    if (!(h > 0) || !(maxCpu > 0) || h > maxCpu) continue;

    const simForm = {
      ...baseForm,
      cooling: 'air',
      cpuH: h,
      gpuT: caseObj.hasSpine ? maxGpuT : (baseForm.gpuT || 40)
    };
    const simRes = checkCompatibility(caseObj, simForm, activeModeId);
    if (!simRes.fits) continue;

    const coolerVector = buildAirCoolerVector(c.name, h, maxCpu);
    const similarityScore = weightedProximity(
      new Float32Array([1.0, 1.0, 1.0]),
      coolerVector,
      [2.0, 1.5, 1.0]
    );
    insertTopK(
      coolerTop,
      { rawItem: c.rawItem, name: c.name, height: h, headroom: maxCpu - h, score: similarityScore },
      16,
      (a, b) => b.score - a.score || a.headroom - b.headroom
    );
  }

  const uniqueCoolers = [];
  const seenCoolerKeys = new Set();
  for (const c of coolerTop) {
    const normKey = c.name.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (seenCoolerKeys.has(normKey)) continue;
    seenCoolerKeys.add(normKey);
    uniqueCoolers.push(c);
    if (uniqueCoolers.length >= 4) break;
  }

  const fanThick = baseForm.radFan === 'slim' ? 15 : (baseForm.radFan === 'thick' || baseForm.radFan === '30mm' ? 30 : 25);
  const aioTop = [];
  if (maxAio > 0) {
    for (const a of index.aios) {
      const { radSize, pumpHeight, radThick, radLen } = a;
      if (!(radSize > 0) || radSize > maxAio) continue;

      const lowerName = a.name.toLowerCase();
      if (lowerName.includes('liquid freezer') || lowerName.includes('freezer ii') || lowerName.includes('freezer iii') ||
          lowerName.includes('ek-') || lowerName.includes('ek ') || lowerName.includes('ekwb')) {
        continue;
      }

      const totalStack = radThick + fanThick;
      if (maxCpu > 0 && maxCpu < 9999 && pumpHeight > maxCpu) continue;
      if (caseObj.top_fan_clearance && totalStack > parseInt(caseObj.top_fan_clearance, 10)) continue;
      if (caseObj._dims && caseObj._dims[0] > 0 && radLen > (caseObj._dims[0] + 10)) continue;

      const simForm = {
        ...baseForm,
        cooling: 'liquid',
        rad: radSize,
        radFan: baseForm.radFan || 'standard',
        gpuT: caseObj.hasSpine ? maxGpuT : (baseForm.gpuT || 40)
      };
      const simRes = checkCompatibility(caseObj, simForm, activeModeId);
      if (!simRes.fits) continue;

      const aioVector = buildAioVector(a.name, radSize, maxAio, radThick, pumpHeight);
      const similarityScore = weightedProximity(
        new Float32Array([1.0, 1.0, 1.0, 1.0]),
        aioVector,
        [2.5, 2.0, 1.0, 1.0]
      );
      insertTopK(
        aioTop,
        { rawItem: a.rawItem, name: a.name, radSize, pumpHeight, radThick, radLen, totalStack, score: similarityScore },
        16,
        (x, y) => y.score - x.score
      );
    }
  }

  const uniqueAios = [];
  const seenAioKeys = new Set();
  for (const a of aioTop) {
    const normKey = a.name.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (seenAioKeys.has(normKey)) continue;
    seenAioKeys.add(normKey);
    uniqueAios.push(a);
    if (uniqueAios.length >= 4) break;
  }

  // GPU catalog is the largest dataset. Do cheap numeric pruning first, only run
  // the deterministic engine for plausible candidates, and retain a bounded top-K
  // instead of allocating + sorting the full result set every render.
  const nvidiaTop = [];
  const radeonTop = [];
  for (const g of index.gpus) {
    if (!(g.len > 0) || !(g.thick > 0)) continue;
    if (maxGpuL > 0 && g.len > maxGpuL) continue;
    if (maxGpuT > 0 && g.thick > maxGpuT) continue;
    if (maxGpuH > 0 && g.height > maxGpuH) continue;
    if (!g.isNvidia && !g.isRadeon) continue;

    const simForm = {
      ...baseForm,
      gpuL: g.len,
      gpuT: g.thick,
      gpuH: g.height,
      cpuH: caseObj.hasSpine ? maxCpu : (baseForm.cpuH || 47)
    };
    const simRes = checkCompatibility(caseObj, simForm, activeModeId);
    if (!simRes.fits) continue;

    const gpuVector = buildGpuVector(g.name, g.len, g.thick, maxGpuL, maxGpuT);
    if (gpuVector[0] <= 0) continue;

    const similarityScore = weightedProximity(
      new Float32Array([1.0, 1.0, 1.0]),
      gpuVector,
      [3.0, 1.5, 1.0]
    );
    const candidate = {
      rawItem: g.rawItem,
      name: g.name,
      len: g.len,
      thick: g.thick,
      height: g.height,
      slotClass: g.slotClass > 0 ? g.slotClass : null,
      headroom: maxGpuL - g.len,
      score: similarityScore,
      isRadeon: g.isRadeon,
      isNvidia: g.isNvidia
    };
    const compareGpu = (a, b) => b.score - a.score || a.headroom - b.headroom;
    if (g.isNvidia) insertTopK(nvidiaTop, candidate, 16, compareGpu);
    if (g.isRadeon) insertTopK(radeonTop, candidate, 16, compareGpu);
  }

  const takeUnique = (items, limit) => {
    const out = [];
    const seen = new Set();
    for (const item of items) {
      const key = item.name.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(item);
      if (out.length >= limit) break;
    }
    return out;
  };

  return rememberRecommendation(recCacheKey, {
    validCoolers: uniqueCoolers,
    validAios: uniqueAios,
    validRtxGpus: takeUnique(nvidiaTop, 3),
    validRadeonGpus: takeUnique(radeonTop, 3)
  });
}

function uniqueByName(items = [], limit = 5) {
  const out = [];
  const seen = new Set();
  for (const item of items) {
    const key = String(item?.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(item);
    if (out.length >= limit) break;
  }
  return out;
}

function getNearestPsuCandidates(caseObj, formInputs, evalResult) {
  const index = getRecommendationHardwareIndex();
  if (!index || !caseObj) return [];
  const activeModeId = getCaseModeSelection(caseObj, 'default');
  const specs = evalResult?.activeSpecs || getEffectiveSpecs(caseObj, activeModeId);
  const preferred = normalizePsuFormFactor(formInputs?.psu || '');
  const maxPsu = Number(specs.maxPSU || 0);
  const candidates = [];

  const fitBySignature = new Map();
  for (const p of index.psus) {
    if (!p.formFactor) continue;
    const sig = `${p.formFactor}|${p.length || 0}`;
    let deterministic = fitBySignature.get(sig);
    if (!deterministic) {
      const testForm = { ...formInputs, psu: p.formFactor, psuL: p.length || undefined };
      deterministic = checkCompatibility(caseObj, testForm, activeModeId);
      fitBySignature.set(sig, deterministic);
    }
    if (!deterministic.fits) continue;
    if (maxPsu > 0 && p.length > 0 && p.length > maxPsu) continue;
    const headroom = maxPsu > 0 && p.length > 0 ? maxPsu - p.length : null;
    const preference = p.formFactor === preferred ? 100000 : 0;
    const closeness = headroom === null ? (p.length || 0) : (10000 - Math.max(0, headroom));
    candidates.push({ ...p, headroom, deterministic, score: preference + closeness });
  }
  candidates.sort((a, b) => b.score - a.score || String(a.name).localeCompare(String(b.name)));
  return uniqueByName(candidates, 5);
}

function getNearestMotherboardCandidates(caseObj, formInputs) {
  const index = getRecommendationHardwareIndex();
  if (!index || !caseObj) return [];
  const activeModeId = getCaseModeSelection(caseObj, 'default');
  const preferred = normalizeMoboFormFactor(formInputs?.mobo || 'ITX');
  const ffFit = new Map();
  const candidates = [];

  for (const board of index.motherboards) {
    const ff = board.formFactor;
    if (!ff) continue;
    if (!ffFit.has(ff)) {
      const deterministic = checkCompatibility(caseObj, { ...formInputs, mobo: ff }, activeModeId);
      ffFit.set(ff, deterministic);
    }
    const deterministic = ffFit.get(ff);
    if (!deterministic?.fits) continue;
    const score = (ff === preferred ? 10000 : 0) + (ff === 'MATX' ? 300 : ff === 'ITX' ? 200 : 100);
    candidates.push({ ...board, deterministic, score });
  }
  candidates.sort((a, b) => b.score - a.score || String(a.name).localeCompare(String(b.name)));
  return uniqueByName(candidates, 5);
}

function signedMm(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n >= 0 ? '+' : ''}${Math.round(n * 10) / 10}mm`;
}

function selectedReferenceLabel(type, formInputs) {
  const kind = type === 'air' ? 'cpu' : (type === 'board' ? 'mobo' : type);
  const selected = getSelectedComponentIdentity(kind);
  if (selected?.name) return selected.name;
  if (type === 'psu') return `${formInputs?.psu || 'SFX'} requirement`;
  if (type === 'board') return `${formInputs?.mobo || 'ITX'} motherboard requirement`;
  if (type === 'gpu') return `${formInputs?.gpuL || 304}×${formInputs?.gpuT || 40}×${formInputs?.gpuH || 137}mm GPU requirement`;
  if (type === 'air') return `${formInputs?.cpuH || 47}mm air-cooler requirement`;
  if (type === 'aio') return `${formInputs?.rad || 240}mm AIO requirement`;
  return 'current build';
}

function candidateMetrics(candidate, type, specs = {}) {
  if (type === 'gpu') {
    const bits = [];
    if (Number(specs.maxGPU) > 0) bits.push(`L ${signedMm(Number(specs.maxGPU) - Number(candidate.len || 0))}`);
    if (Number(specs.maxGPUT) > 0) bits.push(`T ${signedMm(Number(specs.maxGPUT) - Number(candidate.thick || 0))}`);
    if (Number(specs.maxGPUH) > 0) bits.push(`H ${signedMm(Number(specs.maxGPUH) - Number(candidate.height || 0))}`);
    return bits.join(' · ') || `${candidate.len || '?'}×${candidate.thick || '?'}×${candidate.height || '?'}mm`;
  }
  if (type === 'air') {
    return Number(specs.maxCPU) > 0
      ? `H ${signedMm(Number(specs.maxCPU) - Number(candidate.height || 0))}`
      : `${candidate.height || '?'}mm`;
  }
  if (type === 'aio') {
    const delta = Number(specs.maxAIO) > 0 ? signedMm(Number(specs.maxAIO) - Number(candidate.radSize || 0)) : '—';
    return `${candidate.radSize || '?'}mm RAD · ${delta} size`;
  }
  if (type === 'psu') {
    const headroom = candidate.headroom;
    return [
      candidate.formFactor,
      candidate.length ? `L ${candidate.length}mm` : '',
      Number.isFinite(headroom) ? `${signedMm(headroom)} length` : '',
      candidate.wattage ? `${candidate.wattage}W` : ''
    ].filter(Boolean).join(' · ');
  }
  if (type === 'board') {
    return [candidate.formFactor, candidate.socket, candidate.chipset].filter(Boolean).join(' · ');
  }
  if (type === 'ram') {
    return [
      candidate.memoryType,
      Number(candidate.height) > 0 ? `${Math.round(Number(candidate.height) * 10) / 10}mm` : ''
    ].filter(Boolean).join(' · ');
  }
  return '';
}

function candidateCatalogId(candidate) {
  return String(candidate?.catalogId || catalogIdOf(candidate?.rawItem) || '').trim();
}

function renderInsightResultCards(container, candidates, type, badges, specs, sourceLabel) {
  if (!candidates.length) {
    container.innerHTML = `
      <div class="sff-near-fit-empty">
        No deterministic ${escapeHtml(type.toUpperCase())} candidates are available for this case/state.
      </div>`;
    return;
  }

  container.innerHTML = candidates.map((candidate, idx) => {
    const badge = badges[idx] || { text: 'VERIFIED', class: 'fit-pass' };
    const id = candidateCatalogId(candidate);
    return `
      <div class="sff-near-fit-result" data-insight-result data-type="${escapeHtml(type)}"
           data-name="${escapeHtml(candidate.name)}" data-catalog-id="${escapeHtml(id)}">
        <div class="sff-near-fit-result-top">
          <span class="sff-near-fit-result-label">${escapeHtml(candidate.name)}</span>
          <button type="button" class="sff-near-fit-load" data-insight-load>LOAD</button>
        </div>
        <div class="sff-near-fit-metrics">${escapeHtml(candidateMetrics(candidate, type, specs))}</div>
        <div class="sff-near-fit-topology">
          ${escapeHtml(sourceLabel)} · <span class="${escapeHtml(badge.class)}">${escapeHtml(badge.text)}</span>
        </div>
      </div>`;
  }).join('');

  container.querySelectorAll('[data-insight-load]').forEach(button => {
    button.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      const row = button.closest('[data-insight-result]');
      if (!row) return;
      loadInsightChoice(
        row.getAttribute('data-type') || type,
        row.getAttribute('data-name') || '',
        row.getAttribute('data-catalog-id') || ''
      );
    });
  });
}

function getComboboxChoice(containerId, name, catalogId = '') {
  const container = document.getElementById(containerId);
  if (!container) return null;
  const byModel = container._sffFindBestItem?.({ id: catalogId, catalogId, name });
  if (byModel) return byModel;
  container._sffEnsureOptions?.();
  const exactId = String(catalogId || '').trim().toLowerCase();
  const items = [...container.querySelectorAll('.combobox-item')];
  if (exactId) {
    // Catalog-backed recommendations/popularity must never degrade from an exact
    // identity into a fuzzy marketing-name match.
    return items.find(el => String(el.getAttribute('data-catalog-id') || '').trim().toLowerCase() === exactId) || null;
  }
  const wanted = String(name || '').toLowerCase();
  return items.find(el => wanted && el.textContent.toLowerCase().includes(wanted)) || null;
}

function loadInsightChoice(type, name, catalogId = '') {
  if (type === 'case') {
    const combobox = document.getElementById('caseSearchCombobox');
    const input = combobox?.querySelector('.combobox-input') || document.getElementById('caseSearch');
    const item = getComboboxChoice('caseSearchCombobox', name, catalogId);
    if (item) item.click();
    else if (input) {
      input.value = name;
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
    return;
  }

  const map = {
    gpu: 'gpuCombobox',
    air: 'cpuCoolerCombobox',
    aio: 'aioCoolerCombobox',
    board: 'moboCombobox',
    psu: 'psuCombobox',
    ram: 'ramCombobox'
  };
  if (map[type]) {
    const item = getComboboxChoice(map[type], name, catalogId);
    if (item) {
      item.click();
      return;
    }
    if (type !== 'psu') return;
  }

  if (type === 'psu') {
    const index = getRecommendationHardwareIndex();
    const candidate = index?.psus.find(p =>
      (catalogId && p.catalogId === catalogId) || p.name === name
    );
    if (!candidate) return;
    const btn = [...document.querySelectorAll('#psuSegmented .segment-btn')]
      .find(node => normalizePsuFormFactor(node.getAttribute('data-value')) === candidate.formFactor);
    if (btn) btn.click();
    setSelectedComponent('psu', {
      name: candidate.name,
      form_factor: candidate.formFactor,
      length_mm: candidate.length || 0,
      width_mm: candidate.width || 0,
      height_mm: candidate.height || 0,
      wattage: candidate.wattage || 0,
      catalogId: candidate.catalogId || ''
    });
    state.portableBuildHardware.psu = candidate.catalogId ? { id: candidate.catalogId } : null;
    window.evaluateCompatibility?.(true);
  }
}

function updateInsightsVisibility() {
  document.querySelectorAll('[data-insights-mode]').forEach(btn => {
    const active = btn.getAttribute('data-insights-mode') === insightsMode;
    btn.classList.toggle('active', active);
    btn.setAttribute('aria-selected', String(active));
  });
  document.querySelectorAll('[data-insights-panel]').forEach(panel => {
    panel.hidden = panel.getAttribute('data-insights-panel') !== insightsMode;
  });
  document.querySelectorAll('[data-near-fit-type]').forEach(btn => {
    btn.classList.toggle('active', btn.getAttribute('data-near-fit-type') === nearFitType);
  });
  document.querySelectorAll('[data-popular-type]').forEach(btn => {
    btn.classList.toggle('active', btn.getAttribute('data-popular-type') === popularType);
  });
}

export function initHardwareInsightsUi() {
  if (insightsBound) return;
  const root = document.getElementById('sffHardwareInsightsDeck');
  if (!root) return;
  insightsBound = true;

  root.addEventListener('click', event => {
    const modeButton = event.target.closest('[data-insights-mode]');
    if (modeButton) {
      insightsMode = modeButton.getAttribute('data-insights-mode') === 'popular' ? 'popular' : 'near';
      updateInsightsVisibility();
      if (insightsMode === 'near') {
        renderHardwareRecommendations(lastNearContext.caseObj, lastNearContext.formInputs, lastNearContext.evalResult);
      } else {
        renderPopularHardwarePanel(lastPopularContext.activeCase, lastPopularContext.currentForm);
      }
      return;
    }

    const nearButton = event.target.closest('[data-near-fit-type]');
    if (nearButton) {
      nearFitType = nearButton.getAttribute('data-near-fit-type') || 'gpu';
      updateInsightsVisibility();
      renderHardwareRecommendations(lastNearContext.caseObj, lastNearContext.formInputs, lastNearContext.evalResult);
      return;
    }

    const popButton = event.target.closest('[data-popular-type]');
    if (popButton) {
      popularType = popButton.getAttribute('data-popular-type') || 'gpu';
      updateInsightsVisibility();
      renderPopularHardwarePanel(lastPopularContext.activeCase, lastPopularContext.currentForm);
      return;
    }

    if (event.target.closest('[data-near-fit-refresh]')) {
      hardwareRecommendationCache.clear();
      renderHardwareRecommendations(lastNearContext.caseObj, lastNearContext.formInputs, lastNearContext.evalResult);
    }
  });

  updateInsightsVisibility();
}

export async function renderHardwareRecommendations(caseObj, formInputs, evalResult) {
  lastNearContext = { caseObj, formInputs, evalResult };
  const container = document.getElementById('sffHardwareRecommendations');
  if (!container) return;
  if (insightsMode !== 'near') return;

  const renderGeneration = ++hardwareRecommendationRenderGeneration;
  if (!caseObj) {
    container.innerHTML = `
      <div class="sff-near-fit-empty">
        Select a case to prospectively verify near-fit hardware against its current deterministic constraints.
      </div>`;
    const ref = document.querySelector('[data-near-fit-reference]');
    if (ref) ref.textContent = 'Choose a case to activate deterministic prospective fit.';
    return;
  }

  const activeModeId = getCaseModeSelection(caseObj, 'default');
  const specs = evalResult?.activeSpecs || getEffectiveSpecs(caseObj, activeModeId);
  const ref = document.querySelector('[data-near-fit-reference]');
  if (ref) ref.textContent = `Closest deterministic fits to ${selectedReferenceLabel(nearFitType, formInputs)}`;

  let candidates = [];
  if (nearFitType === 'psu') {
    candidates = getNearestPsuCandidates(caseObj, formInputs, evalResult);
  } else if (nearFitType === 'board') {
    candidates = getNearestMotherboardCandidates(caseObj, formInputs);
  } else {
    const recs = getTopNearestHardware(caseObj, formInputs, evalResult);
    if (nearFitType === 'air') candidates = recs?.validCoolers || [];
    else if (nearFitType === 'aio') candidates = recs?.validAios || [];
    else {
      candidates = uniqueByName(
        [...(recs?.validRtxGpus || []), ...(recs?.validRadeonGpus || [])]
          .sort((a, b) => Number(b.score || 0) - Number(a.score || 0)),
        5
      );
    }
  }

  let prospective = [];
  if (nearFitType === 'gpu') {
    prospective = candidates.map(candidate => ({
      candidate, type: 'gpu',
      testForm: { ...formInputs, gpuL: candidate.len, gpuT: candidate.thick, gpuH: candidate.height || 137 },
      deterministic: checkCompatibility(caseObj, { ...formInputs, gpuL: candidate.len, gpuT: candidate.thick, gpuH: candidate.height || 137 }, activeModeId)
    }));
  } else if (nearFitType === 'air') {
    prospective = candidates.map(candidate => {
      const testForm = { ...formInputs, cooling: 'air', cpuH: candidate.height };
      return { candidate, type: 'air', testForm, deterministic: checkCompatibility(caseObj, testForm, activeModeId) };
    });
  } else if (nearFitType === 'aio') {
    prospective = candidates.map(candidate => {
      const testForm = { ...formInputs, cooling: 'liquid', rad: candidate.radSize };
      return { candidate, type: 'aio', testForm, deterministic: checkCompatibility(caseObj, testForm, activeModeId) };
    });
  } else {
    prospective = candidates.map(candidate => ({
      candidate,
      type: nearFitType,
      testForm: formInputs,
      deterministic: candidate.deterministic || { fits: true, warns: [], unknowns: [] }
    }));
  }

  const badges = ['gpu', 'air', 'aio'].includes(nearFitType)
    ? await evaluateEdgeAiBatch(caseObj, prospective, 'Near Fit Batched ML')
    : prospective.map(item => ({
        text: item.deterministic?.unknowns?.length ? 'VERIFY' : 'VERIFIED',
        class: item.deterministic?.unknowns?.length ? 'fit-warn' : 'fit-pass'
      }));

  if (renderGeneration !== hardwareRecommendationRenderGeneration) return;
  renderInsightResultCards(container, candidates, nearFitType, badges, specs, 'DETERMINISTIC');
}

const POPULAR_CATALOG_KIND = Object.freeze({
  case: 'case',
  gpu: 'gpu',
  air: 'aircooler',
  aio: 'aio',
  psu: 'psu',
  board: 'motherboard',
  ram: 'ram'
});

const EMPTY_POPULAR_V2 = Object.freeze({
  case: [], gpu: [], air: [], aio: [], psu: [], board: [], ram: []
});

function popularItemCandidate(rawItem, type, activeCase, currentForm, activity = {}) {
  if (type === 'case') {
    return {
      rawItem,
      name: getHwName(rawItem, 'case'),
      catalogId: catalogIdOf(rawItem),
      metrics: rawItem?._volume ? `${rawItem._volume}L` : '',
      deterministic: null,
      activity
    };
  }

  const displayType = type === 'board' ? 'mobo' : type;
  const candidate = {
    rawItem,
    name: getHwName(rawItem, displayType),
    catalogId: catalogIdOf(rawItem),
    activity
  };
  let testForm = { ...currentForm };

  if (type === 'gpu') {
    candidate.len = parseSpecNum(findHwVal(rawItem, ['length', 'gpulength', 'len', 'lengthmm']), 304);
    candidate.thick = parseSpecNum(findHwVal(rawItem, ['thickness', 'thick', 'gputhickness', 'thicknessmm']), 40);
    candidate.height = parseSpecNum(findHwVal(rawItem, ['height', 'gpuheight', 'width', 'heightmm']), 137);
    testForm = { ...testForm, gpuL: candidate.len, gpuT: candidate.thick, gpuH: candidate.height };
  } else if (type === 'air') {
    candidate.height = parseSpecNum(findHwVal(rawItem, ['height', 'coolerheight', 'size', 'heightmm']), 47);
    testForm = { ...testForm, cooling: 'air', cpuH: candidate.height };
  } else if (type === 'aio') {
    candidate.radSize = parseSpecNum(findHwVal(rawItem, ['radiatortype', 'type', 'size', 'radiatorsize', 'radiator']), 240);
    testForm = { ...testForm, cooling: 'liquid', rad: candidate.radSize };
  } else if (type === 'psu') {
    candidate.formFactor = normalizePsuFormFactor(findHwVal(rawItem, ['form_factor', 'formfactor', 'psu_type', 'type', 'size', 'format']));
    candidate.length = parseSpecNum(findHwVal(rawItem, ['length_mm', 'length', 'psulength', 'depth', 'depth_mm']), 0);
    candidate.width = parseSpecNum(findHwVal(rawItem, ['width_mm', 'width']), 0);
    candidate.height = parseSpecNum(findHwVal(rawItem, ['height_mm', 'height']), 0);
    candidate.wattage = parseSpecNum(findHwVal(rawItem, ['wattage', 'watts', 'power']), 0);
    if (candidate.formFactor) {
      testForm = { ...testForm, psu: candidate.formFactor, psuL: candidate.length || testForm.psuL || 0 };
    }
  } else if (type === 'board') {
    candidate.formFactor = normalizeMoboFormFactor(findHwVal(rawItem, ['form_factor', 'formfactor', 'size', 'type']));
    if (!candidate.formFactor) {
      const identity = `${candidate.name} ${findHwVal(rawItem, ['model', 'name'])}`.toUpperCase();
      if (/MICRO[- ]?ATX|\bMATX\b|M-ATX/.test(identity)) candidate.formFactor = 'MATX';
      else if (/MINI[- ]?ITX|\bITX\b/.test(identity)) candidate.formFactor = 'ITX';
      else if (/\bDTX\b/.test(identity)) candidate.formFactor = 'DTX';
      else if (/\bATX\b/.test(identity)) candidate.formFactor = 'ATX';
    }
    candidate.socket = String(findHwVal(rawItem, ['socket']) || '').trim();
    candidate.chipset = String(findHwVal(rawItem, ['chipset']) || '').trim();
    if (candidate.formFactor) testForm = { ...testForm, mobo: candidate.formFactor };
  } else if (type === 'ram') {
    candidate.height = parseSpecNum(findHwVal(rawItem, ['height_mm', 'height', 'ram_height_mm', 'ramheight']), 0);
    candidate.memoryType = String(findHwVal(rawItem, ['memory_type', 'memorytype', 'ddr', 'type']) || '').trim().toUpperCase();
  }

  const activeModeId = activeCase ? getCaseModeSelection(activeCase, 'default') : 'default';
  candidate.testForm = testForm;
  candidate.deterministic = activeCase && type !== 'ram'
    ? checkCompatibility(activeCase, testForm, activeModeId)
    : null;
  return candidate;
}

function deterministicPopularityBadge(candidate) {
  const deterministic = candidate?.deterministic;
  if (!deterministic) return null;
  if (!deterministic.fits) return { text: 'NO FIT', class: 'fit-fail' };
  if (deterministic.unknowns?.length) return { text: 'VERIFY', class: 'fit-warn' };
  return { text: 'VERIFIED', class: 'fit-pass' };
}

function activityEvidenceText(activity = {}) {
  const score = Number(activity.score || 0);
  const selections = Math.max(0, Number(activity.selections || 0));
  const shares = Math.max(0, Number(activity.shares || 0));
  const builds = Math.max(0, Number(activity.builds || 0));
  const verifiedBuilds = Math.max(0, Number(activity.verified_builds || 0));
  const bits = [`ACTIVITY ${score >= 10 ? Math.round(score) : Math.round(score * 10) / 10}`];
  if (selections) bits.push(`${selections} SELECT${selections === 1 ? '' : 'S'}`);
  if (shares) bits.push(`${shares} SHARE${shares === 1 ? '' : 'S'}`);
  if (builds) bits.push(`${builds} BUILD${builds === 1 ? '' : 'S'}`);
  if (verifiedBuilds) bits.push(`${verifiedBuilds} VERIFIED`);
  return bits.join(' · ');
}

function resolvePopularCandidates(entries, type, activeCase, currentForm, limit = 5) {
  const expectedKind = POPULAR_CATALOG_KIND[type];
  if (!expectedKind) return [];
  const resolved = [];
  const seen = new Set();
  for (const activity of Array.isArray(entries) ? entries : []) {
    const id = String(activity?.catalog_id || '').trim().toLowerCase();
    if (!id || seen.has(id)) continue;
    const hit = resolveCatalogId(id, expectedKind);
    if (!hit?.record) continue;
    const candidate = popularItemCandidate(hit.record, type, activeCase, currentForm || {}, activity);
    if (!candidate?.name || !candidate.catalogId) continue;
    seen.add(id);
    resolved.push(candidate);
    if (resolved.length >= limit) break;
  }
  return resolved;
}

function popularReferenceText(type, activeCase) {
  const label = type.toUpperCase();
  if (!activeCase) return `Popularity-ranked ${label} choices from recent SFF.GG activity and published builds.`;
  if (type === 'case') return 'Popularity-ranked cases from recent SFF.GG activity and published builds.';
  if (type === 'ram') return `Popularity-ranked RAM choices · exact catalog identity preserved when loaded.`;
  return `Popularity-ranked ${label} choices · fit checked for ${activeCase.name}`;
}

export async function renderPopularHardwarePanel(activeCase, currentForm) {
  lastPopularContext = { activeCase, currentForm };
  const container = document.getElementById('sffPopularHardware');
  if (!container) return;
  if (insightsMode !== 'popular') return;

  const renderGeneration = ++popularHardwareRenderGeneration;
  if (!state.fetchedCasesGlobal?.length) {
    container.innerHTML = `<div class="sff-near-fit-empty">Popular hardware data is still loading.</div>`;
    return;
  }

  let popularData = EMPTY_POPULAR_V2;
  const now = Date.now();
  if (popularTelemetryCache.value && (now - popularTelemetryCache.fetchedAt) < 5 * 60 * 1000) {
    popularData = popularTelemetryCache.value;
  } else {
    try {
      const response = await fetch('/api/popular-v2.php', {
        cache: 'default',
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        if (Number(data?.schema) === 2 && data?.popular && typeof data.popular === 'object') {
          popularData = { ...EMPTY_POPULAR_V2, ...data.popular };
          popularTelemetryCache = { fetchedAt: now, value: popularData };
        }
      }
    } catch (error) {
      debugRuntimeIssue('Popular Hardware V2', error);
    }
  }

  const candidates = resolvePopularCandidates(
    popularData[popularType], popularType, activeCase, currentForm || {}, 5
  );

  let badges = candidates.map(() => null);
  if (activeCase && ['gpu', 'air', 'aio'].includes(popularType)) {
    badges = await evaluateEdgeAiBatch(activeCase, candidates.map(candidate => ({
      candidate,
      type: popularType,
      testForm: candidate.testForm,
      deterministic: candidate.deterministic
    })), 'Popular Hardware V2 Batched ML');
  } else if (activeCase && ['psu', 'board'].includes(popularType)) {
    badges = candidates.map(deterministicPopularityBadge);
  }

  if (renderGeneration !== popularHardwareRenderGeneration) return;

  const activeSpecs = activeCase
    ? getEffectiveSpecs(activeCase, getCaseModeSelection(activeCase, 'default'))
    : {};
  const ref = document.querySelector('[data-popular-reference]');
  if (ref) ref.textContent = popularReferenceText(popularType, activeCase);

  if (!candidates.length) {
    container.innerHTML = `<div class="sff-near-fit-empty">Not enough recent ${escapeHtml(popularType.toUpperCase())} activity evidence yet.</div>`;
    return;
  }

  container.innerHTML = candidates.map((candidate, idx) => {
    const badge = badges[idx];
    const metrics = popularType === 'case'
      ? (candidate.rawItem?._volume ? `${candidate.rawItem._volume}L` : 'CASE')
      : candidateMetrics(candidate, popularType, activeSpecs);
    const evidence = activityEvidenceText(candidate.activity);
    const fit = badge
      ? ` · <span class="${escapeHtml(badge.class)}">${escapeHtml(badge.text)}</span>`
      : '';
    return `
      <div class="sff-near-fit-result" data-insight-result data-type="${escapeHtml(popularType)}"
           data-name="${escapeHtml(candidate.name)}" data-catalog-id="${escapeHtml(candidate.catalogId || '')}">
        <div class="sff-near-fit-result-top">
          <span class="sff-near-fit-result-label">${escapeHtml(candidate.name)}</span>
          <button type="button" class="sff-near-fit-load" data-insight-load>${popularType === 'case' ? 'SELECT' : 'LOAD'}</button>
        </div>
        <div class="sff-near-fit-metrics">${escapeHtml(metrics)}</div>
        <div class="sff-near-fit-topology">${escapeHtml(evidence)}${fit}</div>
      </div>`;
  }).join('');

  container.querySelectorAll('[data-insight-load]').forEach(button => {
    button.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      const row = button.closest('[data-insight-result]');
      if (!row) return;
      loadInsightChoice(
        row.getAttribute('data-type') || popularType,
        row.getAttribute('data-name') || '',
        row.getAttribute('data-catalog-id') || ''
      );
    });
  });
}

